import { Segmented } from '@/components/ui/segmented';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Search, Plus, Calendar, User, Edit, RefreshCw,
  AlertCircle, Loader2, X, MoreHorizontal, CalendarRange,
  FileText, Stethoscope,
  UserCheck, UserX, LayoutList, CalendarDays, Columns3, MessageCircle,
} from 'lucide-react';
import { format, startOfWeek, addDays } from 'date-fns';
import { getAppointmentsByDateRange, updateAppointment, queueCheckin, getDoctors } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import EditAppointmentModal from '@/components/Appointment/EditAppointmentModal';
import PatientFormModal from '@/components/Patient/PatientFormModal';
import AddAppointmentModal from '@/components/Appointment/AddAppointmentModal';
import AppointmentCalendar from '@/components/Appointment/AppointmentCalendar';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { Input } from '@/components/ui/input';
import { StatusChip, type StatusTone } from '@/components/ui/status-chip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { displayAge } from '@/lib/patientAge';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import type { PatientSearchResult } from '@/hooks/usePatientSearch';

type ViewMode = 'list' | 'day' | 'week';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Patient { id: string; name: string; age?: number; gender?: string; phone?: string }

interface Appointment {
  id: string; patient_id: string; date: string; time: string;
  reason: string; status: string; doctor_id?: string;
  // inline patient fields returned by API
  patient_name?: string; patient_age?: number; patient_date_of_birth?: string;
  patient_gender?: string; patient_phone?: string;
  doctor_name?: string;
}

interface AptRow extends Appointment {
  patientName: string;
  patientGender: string; patientPhone: string;
  aptDate: Date; isToday: boolean; isPast: boolean; timeDiff: number;
}

interface Filters {
  dateRange: 'today' | 'tomorrow' | 'week' | 'month' | 'custom';
  customStart: string; customEnd: string;
  status: string; search: string;
}

type ConfirmAction = { id: string; action: 'cancel' | 'no_show' };
type Modal = 'none' | 'appointment' | 'newPatient' | 'editAppointment';

// ─── Status config ────────────────────────────────────────────────────────────

// One label and tone per status — the same five tones as every other list
const STATUS: Record<string, { label: string; tone: StatusTone }> = {
  scheduled:   { label: 'Scheduled',       tone: 'neutral' },
  checked_in:  { label: 'Checked in',      tone: 'accent'  },
  in_progress: { label: 'In consultation', tone: 'accent'  },
  completed:   { label: 'Completed',       tone: 'success' },
  cancelled:   { label: 'Cancelled',       tone: 'neutral' },
  no_show:     { label: 'No-show',         tone: 'warning' },
};
// Still "scheduled" after its day has gone: the patient didn't come and nobody closed the booking
const MISSED = { label: 'Missed', tone: 'warning' as StatusTone };
// Today, past its time and not arrived: call them, or mark a no-show
const LATE = { label: 'Late', tone: 'warning' as StatusTone };
// Still "checked in" after its day has gone: the patient came, but the queue visit was never finished
const NOT_FINISHED = { label: 'Not finished', tone: 'neutral' as StatusTone };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtTime(t: string) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const d = new Date(); d.setHours(h, m);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function fmtRemaining(diff: number) {
  if (diff <= 0) return null;
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** WhatsApp reminder link for a booking still to come (10-digit Indian mobile), or null */
function reminderLink(apt: { patientName: string; patientPhone: string; time: string; doctor_name?: string }, day: string) {
  const digits = (apt.patientPhone || '').replace(/\D/g, '').slice(-10);
  if (digits.length !== 10) return null;
  const doctor = apt.doctor_name ? ` with ${apt.doctor_name.startsWith('Dr.') ? apt.doctor_name : `Dr. ${apt.doctor_name}`}` : '';
  const msg = `Hello ${apt.patientName}, this is a reminder of your appointment ${day} at ${fmtTime(apt.time)}${doctor}.`;
  return `https://wa.me/91${digits}?text=${encodeURIComponent(msg)}`;
}

function loadRange(f: Filters): { start: string; end: string } {
  const todayStr = format(new Date(), "yyyy-MM-dd");
  if (f.dateRange === 'custom' && f.customStart && f.customEnd) {
    return { start: f.customStart, end: f.customEnd };
  }
  // Pre-defined ranges: load today → today+90 so client-side filters always have data
  const end = new Date(); end.setDate(end.getDate() + 90);
  return { start: todayStr, end: format(end, "yyyy-MM-dd") };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AppointmentsPage() {
  useEffect(() => { document.title = 'Appointments | OneCheckUp'; }, []);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  // Front-desk view if user has no clinical profile (doctor_id), regardless of role
  const isFrontDesk = !currentUser?.doctor_id;
  // Booking, editing and cancelling need appointments:write (the server enforces the same);
  // check-in is a queue action, consulting writes a prescription
  const { can } = usePermission();
  const canBook = can('appointments:write');
  const canCheckIn = isFrontDesk && can('queue:write');
  const canConsult = !isFrontDesk && can('prescriptions:write');

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modal, setModal] = useState<Modal>('none');
  const [editingApt, setEditingApt] = useState<AptRow | null>(null);
  const [newPatientForApt, setNewPatientForApt] = useState<Patient | null>(null);
  // What was typed in the booking's patient search — prefills the new-patient form
  const [newPatientSearch, setNewPatientSearch] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState('');

  const [filters, setFilters] = useState<Filters>({
    dateRange: 'today', customStart: '', customEnd: '',
    status: 'all', search: '',
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [checkingInId, setCheckingInId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [newAptPrefill, setNewAptPrefill] = useState<{ date?: string; time?: string }>({});
  // Front desk with several doctors: one doctor's bookings, so the day and week views show their free slots
  const [doctors, setDoctors] = useState<{ id: string; name: string }[]>([]);
  const [doctorId, setDoctorId] = useState('all');
  useEffect(() => {
    if (isFrontDesk) getDoctors().then(d => setDoctors(Array.isArray(d) ? d : [])).catch(() => {});
  }, [isFrontDesk]);
  const ofDoctor = useCallback(<T extends { doctor_id?: string }>(list: T[]) =>
    doctorId === 'all' ? list : list.filter(a => a.doctor_id === doctorId), [doctorId]);

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadData = useCallback(async (f: Filters) => {
    if (!currentUser?.clinic_id) return;
    try {
      const { start, end } = loadRange(f);
      const doctorFilter = currentUser.doctor_id ?? undefined;
      const data = await getAppointmentsByDateRange(start, end, currentUser.clinic_id, doctorFilter);
      if (data) {
        setAppointments(
          data.sort((a: Appointment, b: Appointment) => {
            const da = new Date(`${a.date.split('T')[0]} ${a.time}`);
            const db = new Date(`${b.date.split('T')[0]} ${b.time}`);
            return da.getTime() - db.getTime();
          })
        );
      }
      setError(null);
    } catch {
      setError('Failed to load appointments.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentUser?.clinic_id, currentUser?.role, currentUser?.doctor_id]);

  useEffect(() => { loadData(filters); }, [loadData]);

  useEffect(() => {
    if (searchParams.get('filter') === 'today')
      setFilters(prev => ({ ...prev, dateRange: 'today' }));
  }, [searchParams]);

  // Re-fetch when switching to custom range with both dates set
  useEffect(() => {
    if (filters.dateRange === 'custom' && filters.customStart && filters.customEnd) {
      setRefreshing(true);
      loadData(filters);
    }
  }, [filters.customStart, filters.customEnd, loadData]);

  // Re-fetch when calendar navigates to a new day/week
  useEffect(() => {
    if (viewMode === 'list' || !currentUser?.clinic_id) return;
    const doctorFilter = currentUser.doctor_id ?? undefined;
    let start: string, end: string;
    if (viewMode === 'day') {
      start = end = format(calendarDate, 'yyyy-MM-dd');
    } else {
      const ws = startOfWeek(calendarDate, { weekStartsOn: 1 });
      start = format(ws, 'yyyy-MM-dd');
      end   = format(addDays(ws, 6), 'yyyy-MM-dd');
    }
    setRefreshing(true);
    getAppointmentsByDateRange(start, end, currentUser.clinic_id, doctorFilter)
      .then(data => { if (data) setAppointments(data); })
      .catch(() => {})
      .finally(() => setRefreshing(false));
  }, [calendarDate, viewMode, currentUser?.clinic_id]);

  const handleRefresh = () => {
    setRefreshing(true);
    if (viewMode === 'list') {
      loadData(filters);
    } else {
      // Re-trigger the calendar fetch effect by nudging calendarDate
      setCalendarDate(d => new Date(d));
    }
  };

  // ── Parsing ─────────────────────────────────────────────────────────────────

  const parse = useCallback((apt: Appointment): AptRow => {
    const dateOnly = apt.date.split('T')[0];
    const aptDateTime = new Date(`${dateOnly} ${apt.time}`);
    const now = new Date();
    const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0);
    const aptDate = new Date(dateOnly + 'T00:00:00');
    return {
      ...apt,
      patientName:   apt.patient_name   || 'Unknown',
      patientGender: apt.patient_gender || '',
      patientPhone:  apt.patient_phone  || '',
      aptDate,
      isToday: aptDate.toDateString() === todayMidnight.toDateString(),
      isPast:  aptDateTime < now,
      timeDiff: aptDateTime.getTime() - now.getTime(),
    };
  }, []);

  // ── Filtering ───────────────────────────────────────────────────────────────

  const filtered = useMemo<AptRow[]>(() => {
    let list = ofDoctor(appointments).map(parse);
    const today = new Date(); today.setHours(0, 0, 0, 0);

    switch (filters.dateRange) {
      case 'today':
        list = list.filter(a => a.isToday); break;
      case 'tomorrow': {
        const tmr = new Date(today); tmr.setDate(tmr.getDate() + 1);
        list = list.filter(a => a.aptDate.toDateString() === tmr.toDateString()); break;
      }
      case 'week': {
        const wEnd = new Date(today); wEnd.setDate(wEnd.getDate() + 7);
        list = list.filter(a => a.aptDate >= today && a.aptDate <= wEnd); break;
      }
      case 'month': {
        const mEnd = new Date(today); mEnd.setMonth(mEnd.getMonth() + 1);
        list = list.filter(a => a.aptDate >= today && a.aptDate <= mEnd); break;
      }
      case 'custom':
        if (filters.customStart && filters.customEnd) {
          const s = new Date(filters.customStart + 'T00:00:00');
          const e = new Date(filters.customEnd + 'T23:59:59');
          list = list.filter(a => a.aptDate >= s && a.aptDate <= e);
        }
        break;
    }

    if (filters.status !== 'all') list = list.filter(a => a.status === filters.status);

    if (filters.search.trim()) {
      const q = filters.search.toLowerCase();
      list = list.filter(a =>
        a.patientName.toLowerCase().includes(q) ||
        a.patientPhone.includes(q) ||
        (a.reason && a.reason.toLowerCase().includes(q)) ||
        (isFrontDesk && !!a.doctor_name && a.doctor_name.toLowerCase().includes(q))
      );
    }

    return list;
  }, [appointments, filters, parse, isFrontDesk, ofDoctor]);

  // ── Stats — based on the current filtered view ───────────────────────────────

  const stats = useMemo(() => ({
    total:      filtered.length,
    scheduled:  filtered.filter(a => a.status === 'scheduled').length,
    completed:  filtered.filter(a => a.status === 'completed').length,
    cancelled:  filtered.filter(a => a.status === 'cancelled' || a.status === 'no_show').length,
  }), [filtered]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const executeCancel = async (apt: AptRow) => {
    setConfirmAction(null);
    try {
      await updateAppointment({ ...apt, status: 'cancelled', updated_at: new Date().toISOString() });
      setAppointments(prev => prev.map(a => a.id === apt.id ? { ...a, status: 'cancelled' } : a));
      toast({ title: 'Cancelled', description: `${apt.patientName}'s appointment cancelled` });
    } catch {
      toast({ title: 'Error', description: 'Failed to cancel', variant: 'destructive' });
    }
  };

  const executeNoShow = async (apt: AptRow) => {
    setConfirmAction(null);
    try {
      await updateAppointment({ ...apt, status: 'no_show', updated_at: new Date().toISOString() });
      setAppointments(prev => prev.map(a => a.id === apt.id ? { ...a, status: 'no_show' } : a));
      toast({ title: 'Marked as no-show', description: apt.patientName });
    } catch {
      toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
    }
  };

  const handleCheckIn = async (apt: AptRow | Appointment) => {
    setCheckingInId(apt.id);
    try {
      const entry = await queueCheckin(apt.id);
      setAppointments(prev => prev.map(a => a.id === apt.id ? { ...a, status: 'checked_in' } : a));
      toast({
        title: 'Checked in',
        description: (() => { const n = (apt as AptRow).patientName ?? apt.patient_name ?? ''; return entry ? `Token #${entry.tokenNumber} · ${n}` : n; })(),
      });
    } catch (err: unknown) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Check-in failed', variant: 'destructive' });
    } finally {
      setCheckingInId(null);
    }
  };

  // ── Modal helpers ────────────────────────────────────────────────────────────

  const closeModal = useCallback(() => {
    setModal('none');
    setEditingApt(null);
    setSelectedPatientId('');
    setNewPatientForApt(null);
    setNewAptPrefill({});
  }, []);

  const handlePatientSelected = useCallback((p: PatientSearchResult) => {
    setSelectedPatientId(p.id);
  }, []);

  // The patient form closes itself right after onSuccess; that close must not end the booking
  const justCreatedRef = useRef(false);
  const handleNewPatientClose = useCallback(() => {
    if (justCreatedRef.current) { justCreatedRef.current = false; return; }
    closeModal();
  }, [closeModal]);

  const handlePatientCreated = useCallback((p: Patient) => {
    justCreatedRef.current = true;
    setNewPatientForApt(p);
    setSelectedPatientId(p.id);
    setModal('appointment');
    toast({ title: 'Patient created', description: `${p.name} selected for appointment` });
  }, []);

  // ── Guards ───────────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <Loader2 className="w-7 h-7 text-primary animate-spin mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Loading appointments...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="p-6 text-center py-12">
      <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-3" />
      <p className="text-muted-foreground mb-4">{error}</p>
      <Button onClick={() => loadData(filters)}>Try Again</Button>
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  const multiDay = filters.dateRange !== 'today' && filters.dateRange !== 'tomorrow';
  const figure = (value: React.ReactNode) => <span className="font-semibold tabular-nums text-foreground">{value}</span>;
  const statusParts: React.ReactNode[] = [
    <span key="total">{figure(stats.total)} appointment{stats.total !== 1 ? 's' : ''}</span>,
    <span key="scheduled">{figure(stats.scheduled)} scheduled</span>,
    <span key="completed">{figure(stats.completed)} completed</span>,
    ...(stats.cancelled > 0 ? [<span key="cancelled">{figure(stats.cancelled)} cancelled / no-show</span>] : []),
  ];
  const dayLabel = (d: Date) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((d.getTime() - today.getTime()) / 86_400_000);
    const name = format(d, 'EEEE, d MMM');
    return diff === 0 ? `Today · ${name}` : diff === 1 ? `Tomorrow · ${name}` : name;
  };

  return (
    <div className="prescription-form-container">
    <div className="space-y-4">

      {/* Header — wraps on phones */}
      <PageHeader
        icon={Calendar}
        title="Appointments"
        subtitle={viewMode === 'list'
          ? <span className="flex flex-wrap items-baseline gap-x-1.5">{statusParts.map((p, i) => <React.Fragment key={i}>{i > 0 && <span aria-hidden>·</span>}{p}</React.Fragment>)}</span>
          : undefined}
        actions={<>
          {doctors.length > 1 && (
            <select
              aria-label="Doctor"
              value={doctorId}
              onChange={e => setDoctorId(e.target.value)}
              className="h-8 touch:h-10 max-w-[11rem] rounded-md border border-border bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="all">All doctors</option>
              {doctors.map(d => <option key={d.id} value={d.id}>{d.name.startsWith('Dr.') ? d.name : `Dr. ${d.name}`}</option>)}
            </select>
          )}
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="w-8 px-0" title="Refresh" aria-label="Refresh">
            <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
          </Button>
          {/* View toggle */}
          <Segmented<ViewMode>
            aria-label="View"
            options={([
              { mode: 'list', icon: LayoutList,   label: 'List' },
              { mode: 'day',  icon: CalendarDays, label: 'Day'  },
              { mode: 'week', icon: Columns3,     label: 'Week' },
            ] as const).map(({ mode, icon: Icon, label }) => ({
              value: mode,
              title: label,
              label: <><Icon className="w-3.5 h-3.5" /><span className="hidden sm:inline">{label}</span></>,
            }))}
            value={viewMode}
            onChange={setViewMode}
          />
          {canBook && (
            <Button size="sm" onClick={() => setModal('appointment')} className="gap-1.5">
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">New appointment</span><span className="sm:hidden">New</span>
            </Button>
          )}
      </>} />

      {/* Filters — list view only; one row, wraps on phones */}
      {viewMode === 'list' && (
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            aria-label="Date range"
            options={([['today', 'Today'], ['tomorrow', 'Tomorrow'], ['week', 'Next 7 days'], ['month', 'Next month']] as const)
              .map(([value, label]) => ({ value, label }))}
            value={filters.dateRange === 'custom' ? ('' as any) : filters.dateRange}
            onChange={r => { setFilters(prev => ({ ...prev, dateRange: r })); setShowAdvanced(false); }}
            className="max-w-full overflow-x-auto"
          />

          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              aria-label="Search appointments"
              placeholder={isFrontDesk ? "Search patient, phone, doctor or reason…" : "Search patient, phone or reason…"}
              value={filters.search}
              onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="h-8 pl-8 text-sm"
            />
          </div>

          <select
            aria-label="Status"
            value={filters.status}
            onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="h-8 touch:h-10 rounded-md border border-border bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="all">All statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="checked_in">Checked in</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="no_show">No-show</option>
          </select>

          <Button
            variant="outline" size="sm"
            aria-pressed={showAdvanced || filters.dateRange === 'custom'}
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={cn('gap-1.5', (showAdvanced || filters.dateRange === 'custom') && 'border-primary text-primary')}
          >
            <CalendarRange className="w-4 h-4" /> Dates
          </Button>

          {/* Custom date range */}
          {showAdvanced && (
            <div className="flex basis-full flex-wrap items-center gap-2">
              <Input
                type="date"
                aria-label="From"
                value={filters.customStart}
                onChange={e => setFilters(prev => ({ ...prev, customStart: e.target.value, dateRange: 'custom' }))}
                className="h-8 w-[9.5rem] text-sm"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="date"
                aria-label="To"
                value={filters.customEnd}
                onChange={e => setFilters(prev => ({ ...prev, customEnd: e.target.value, dateRange: 'custom' }))}
                className="h-8 w-[9.5rem] text-sm"
              />
              <Button
                variant="ghost" size="sm"
                onClick={() => {
                  setFilters({ dateRange: 'today', customStart: '', customEnd: '', status: 'all', search: '' });
                  setShowAdvanced(false);
                }}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Calendar view */}
      {viewMode !== 'list' && (
        <AppointmentCalendar
          view={viewMode}
          date={calendarDate}
          appointments={ofDoctor(appointments)}
          isFrontDesk={canCheckIn}
          checkingInId={checkingInId}
          onNavigate={setCalendarDate}
          onNewAppointment={(date, time) => {
            if (!canBook) return;
            setNewAptPrefill({ date, time });
            setModal('appointment');
          }}
          onEditAppointment={apt => {
            if (!canBook) return;
            setEditingApt(parse(apt as Appointment));
            setModal('editAppointment');
          }}
          onCheckIn={apt => handleCheckIn(apt as Appointment)}
        />
      )}

      {/* List */}
      {viewMode === 'list' && (filtered.length === 0 ? (
        <div className="rounded-lg border border-border bg-card px-6 py-12 text-center">
          <Calendar className="w-6 h-6 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-semibold text-foreground">No appointments</p>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            {filters.search || filters.status !== 'all'
              ? 'Nothing matches these filters.'
              : 'Nothing is booked for this period.'}
          </p>
          {canBook && (
            <Button variant="outline" size="sm" onClick={() => setModal('appointment')} className="gap-1.5">
              <Plus className="w-4 h-4" /> New appointment
            </Button>
          )}
        </div>
      ) : (
        <ul aria-label="Appointments" className="rounded-lg border border-border bg-card overflow-hidden">
          {filtered.map((apt, i) => {
            const dayGone = !apt.isToday && apt.isPast;
            const cfg = dayGone && apt.status === 'scheduled' ? MISSED
              : apt.isToday && apt.isPast && apt.status === 'scheduled' ? LATE
              : dayGone && apt.status === 'checked_in' ? NOT_FINISHED
              : STATUS[apt.status] ?? STATUS.scheduled;
            const remaining = apt.isToday && apt.status === 'scheduled' && !apt.isPast
              ? fmtRemaining(apt.timeDiff) : null;
            const isUrgent = remaining !== null && apt.timeDiff <= 900_000;
            const isConfirming = confirmAction?.id === apt.id;
            const closed = apt.status === 'cancelled' || apt.status === 'no_show';
            const newDay = multiDay && (i === 0 || filtered[i - 1].aptDate.toDateString() !== apt.aptDate.toDateString());
            const demographics = [displayAge(apt.patient_age, apt.patient_date_of_birth) || null, apt.patientGender || null].filter(Boolean).join(' · ');

            return (
              <React.Fragment key={apt.id}>
                {/* Date divider when the list spans several days */}
                {newDay && (
                  <li aria-hidden className="flex items-center h-8 px-4 border-b border-border bg-muted/40 text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                    {dayLabel(apt.aptDate)}
                  </li>
                )}
                <li className={cn(
                  'flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-1.5 px-4 py-2 min-h-12 border-b border-divider last:border-0',
                  isUrgent && 'bg-highlight/60',
                )}>
                  {/* Time */}
                  <span className={cn('w-[4.5rem] flex-shrink-0 font-mono text-sm tabular-nums', closed ? 'text-muted-foreground' : 'text-foreground font-medium')}>
                    {fmtTime(apt.time)}
                  </span>

                  {/* Patient */}
                  <div className="min-w-0 flex-1">
                    <p className="flex items-baseline gap-2 min-w-0">
                      <span className={cn('truncate text-sm font-medium capitalize', closed ? 'text-muted-foreground line-through decoration-muted-foreground/50' : 'text-foreground')}>
                        {apt.patientName}
                      </span>
                      {demographics && <span className="flex-shrink-0 text-xs text-muted-foreground">{demographics}</span>}
                    </p>
                    <p className="flex items-center gap-x-2 text-xs text-muted-foreground min-w-0">
                      {/* The desk books for every doctor, so say whose patient this is */}
                      {isFrontDesk && apt.doctor_name && (
                        <>
                          <span className="flex-shrink-0 text-foreground/80">{apt.doctor_name.startsWith('Dr.') ? apt.doctor_name : `Dr. ${apt.doctor_name}`}</span>
                          {(apt.reason || apt.patientPhone) && <span aria-hidden>·</span>}
                        </>
                      )}
                      {apt.reason && <span className="truncate">{apt.reason}</span>}
                      {apt.reason && apt.patientPhone && <span aria-hidden>·</span>}
                      {apt.patientPhone && (
                        <a href={`tel:${apt.patientPhone}`} className="touch-target flex-shrink-0 tabular-nums hover:text-primary">
                          {apt.patientPhone}
                        </a>
                      )}
                      {/* The day-before (or same-day) reminder most clinics send on WhatsApp */}
                      {apt.status === 'scheduled' && !dayGone && (() => {
                        const tmr = new Date(); tmr.setHours(0, 0, 0, 0); tmr.setDate(tmr.getDate() + 1);
                        const day = apt.isToday ? 'today' : apt.aptDate.toDateString() === tmr.toDateString() ? 'tomorrow' : `on ${format(apt.aptDate, 'd MMM')}`;
                        const link = reminderLink(apt, day);
                        return link && (
                          <a href={link} target="_blank" rel="noopener noreferrer" title="WhatsApp reminder" aria-label={`WhatsApp reminder to ${apt.patientName}`}
                            className="touch-target inline-flex flex-shrink-0 items-center text-muted-foreground hover:text-primary">
                            <MessageCircle className="w-3.5 h-3.5" />
                          </a>
                        );
                      })()}
                    </p>
                  </div>

                  {/* Status — fixed width from md so the chips line up in a column */}
                  <div className="flex items-center gap-2 flex-shrink-0 md:w-40">
                    <StatusChip tone={cfg.tone}>{cfg.label}</StatusChip>
                    {remaining && (
                      <span className={cn('text-xs tabular-nums', isUrgent ? 'font-semibold text-highlight-foreground' : 'text-muted-foreground')}>
                        in {remaining}
                      </span>
                    )}
                  </div>

                  {/* Actions — one main action, edit, and the rest in a menu */}
                  <div className="flex items-center justify-end gap-1 flex-shrink-0 ml-auto md:w-[12.5rem]">
                    {isConfirming ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-foreground">
                          {confirmAction.action === 'cancel' ? 'Cancel this appointment?' : 'Mark as no-show?'}
                        </span>
                        <Button
                          size="sm" variant="destructive"
                          onClick={() => confirmAction.action === 'cancel' ? executeCancel(apt) : executeNoShow(apt)}
                        >
                          {confirmAction.action === 'cancel' ? 'Cancel it' : 'No-show'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setConfirmAction(null)}>Keep</Button>
                      </div>
                    ) : (
                      <>
                        {/* Consult — doctor only */}
                        {apt.status === 'scheduled' && apt.isToday && canConsult && (
                          <Button
                            size="sm"
                            variant={isUrgent ? 'default' : 'outline'}
                            onClick={() => navigate(`/patients/${apt.patient_id}?openPrescription=true`)}
                            className="gap-1.5"
                          >
                            <Stethoscope className="w-3.5 h-3.5" /> Consult
                          </Button>
                        )}

                        {/* Check in — front desk only, on the day */}
                        {apt.status === 'scheduled' && apt.isToday && canCheckIn && (
                          <Button
                            size="sm"
                            variant="default"
                            className="gap-1.5"
                            disabled={checkingInId === apt.id}
                            onClick={() => handleCheckIn(apt)}
                          >
                            {checkingInId === apt.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <UserCheck className="w-3.5 h-3.5" />}
                            Check in
                          </Button>
                        )}

                        {apt.status === 'scheduled' && canBook && (
                          <Button
                            variant="ghost" size="sm" className="w-8 px-0 text-muted-foreground"
                            onClick={() => { setEditingApt(apt); setModal('editAppointment'); }}
                            title="Edit appointment" aria-label={`Edit ${apt.patientName}'s appointment`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="w-8 px-0 text-muted-foreground" aria-label={`More actions for ${apt.patientName}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onSelect={() => navigate(isFrontDesk ? `/frontdesk/patients/${apt.patient_id}` : `/patients/${apt.patient_id}`)}>
                              <User className="w-4 h-4 mr-2" /> View patient
                            </DropdownMenuItem>
                            {!isFrontDesk && (
                              <DropdownMenuItem onSelect={() => navigate(`/prescriptions?patient=${apt.patient_id}`)}>
                                <FileText className="w-4 h-4 mr-2" /> Prescriptions
                              </DropdownMenuItem>
                            )}
                            {apt.status === 'scheduled' && canBook && (
                              <>
                                <DropdownMenuSeparator />
                                {isFrontDesk && (
                                  <DropdownMenuItem onSelect={() => setConfirmAction({ id: apt.id, action: 'no_show' })}>
                                    <UserX className="w-4 h-4 mr-2" /> Mark as no-show
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onSelect={() => setConfirmAction({ id: apt.id, action: 'cancel' })} className="text-destructive focus:text-destructive">
                                  <X className="w-4 h-4 mr-2" /> Cancel appointment
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </>
                    )}
                  </div>
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      ))}

      {/* Modals */}
      {modal === 'appointment' && (
        <AddAppointmentModal
          isOpen
          onClose={closeModal}
          patientId={selectedPatientId}
          patients={newPatientForApt ? [newPatientForApt as any] : []}
          showPatientSelection
          onPatientSelected={handlePatientSelected}
          onCreateNewPatient={can('patients:write') ? term => { setNewPatientSearch(term); setModal('newPatient'); } : undefined}
          onAdded={() => { loadData(filters); closeModal(); }}
          initialDate={newAptPrefill.date}
          initialTime={newAptPrefill.time}
        />
      )}

      {/* The same patient form as everywhere else; "Create" returns to the booking with the patient selected */}
      <PatientFormModal
        isOpen={modal === 'newPatient'}
        onClose={handleNewPatientClose}
        onSuccess={handlePatientCreated}
        mode="create"
        showPrescriptionOption={false}
        initialSearch={newPatientSearch}
      />

      {modal === 'editAppointment' && editingApt && (
        <EditAppointmentModal
          appointment={editingApt}
          isOpen
          onClose={closeModal}
          onUpdated={updated => {
            setAppointments(prev => prev.map(a => a.id === updated.id ? { ...a, ...updated } : a));
            closeModal();
          }}
        />
      )}
    </div>
    </div>
  );
}
